import axios from "@utils/axios-instance";

export type TInteractionType = "videocall" | "call" | "email" | "sms";

/** A file attached to an email: one sent from here, or metadata of one that arrived. */
export type TEmailAttachment = {
    /** Opaque handle: what a send names in `attachments`, and what a download asks for. */
    id: string;
    name: string;
    mime: string;
    /** Bytes. */
    size: number;
};

/** One tracked open or link click of a sent email. */
export type TInteractionEmailEvent = {
    type: "open" | "click";
    /** ISO timestamp, UTC. */
    occurredAt: string;
    /** The clicked link; null on opens. */
    url: string | null;
};

export type TInteractionEmailDetail = {
    subject?: string;
    bodyHtml?: string;
    bodyText?: string;
    fromAddress?: string;
    fromName?: string;
    toAddresses?: string[];
    ccAddresses?: string[];
    bccAddresses?: string[];
    trackingEnabled?: boolean;
    openCount?: number;
    lastOpenedAt?: string;
    clickCount?: number;
    lastClickedAt?: string;
    /**
     * Opens and clicks, oldest first. Empty when not opened yet, and for email
     * sent before the history was kept (those have only the counts). Opens
     * within 15 minutes of a logged one aren't listed; every click is.
     */
    events?: TInteractionEmailEvent[];
    /**
     * The conversation this email belongs to, from its message headers
     * (In-Reply-To / References); a reply shares the thread of the email it
     * answers. Null for email stored before threads were recorded, which is
     * grouped by subject instead.
     */
    threadId?: string | null;
    attachments?: TEmailAttachment[];
};

/** Internal: the call-center call. HubSpot: a meeting (`recordingUrl` is its meeting/calendar link). */
type TInteractionVideocallDetail = {
    duration?: number;
    recordingUrl?: string | null;
    /** Internal: numeric call status. HubSpot: outcome string ("COMPLETED", "SCHEDULED", …). */
    status?: number | string;
    leadRating?: number;
    body?: string;
    /** Internal calls with a recording: the recording page id. */
    recordingId?: string;
};

type TInteractionCallDetail = {
    /** Seconds. */
    duration?: number | null;
    status?: string;
    recordingUrl?: string;
    fromNumber?: string;
    toNumber?: string;
    body?: string;
    /** Internal calls with a recording: the recording page id. */
    recordingId?: string;
};

/** Where a text handled here stands; absent on imports. `received` marks a lead's inbound text. */
export type TSmsStatus = "sent" | "unconfirmed" | "delivered" | "undelivered" | "received";

type TInteractionSmsDetail = {
    body?: string;
    fromNumber?: string;
    toNumber?: string;
    /** `unconfirmed` = the provider never answered, it may have reached the lead. */
    status?: TSmsStatus;
    /** The provider's reason when a sent text ended `undelivered` (e.g. Twilio 30034). */
    errorCode?: number;
};

/** Who handled the interaction: a user, or the HubSpot owner's name when they are not a user here. */
type TInteractionRep = {
    userId: number | null;
    name: string | null;
    avatar: string | null;
};

export type TLeadInteraction = {
    id: number;
    leadId: number;
    rep: TInteractionRep | null;
    type: TInteractionType;
    source: "internal" | "integration";
    direction?: "inbound" | "outbound";
    date: string;
    externalSource?: "hubspot";
    externalId?: string;
    videocall?: TInteractionVideocallDetail;
    call?: TInteractionCallDetail;
    email?: TInteractionEmailDetail;
    sms?: TInteractionSmsDetail;
};

/** One page of a lead's interactions, newest first; `type` narrows to one kind. */
export const getLeadInteractions = async (
    leadId: number,
    page: number,
    pageSize: number,
    type?: TInteractionType
): Promise<{ data: TLeadInteraction[]; totalCount: number }> => {
    const response = await axios.get(`/lead/${leadId}/interactions`, { params: { page, pageSize, type } });
    return { data: response.data.data, totalCount: response.data.meta.totalCount };
};
